import classNames from 'classnames';
import React from 'react';
import * as styles from './KeyboardShortcutsPanel.module.scss';
import Shortcut from './Shortcut';
import RightPanel from '../../app/layout/RightPanel';
import useIsPublicDocumentationView from '../../public/useIsPublicDocumentationView';
import useIsPublicView from '../../utils/useIsPublicView';

interface ShortcutSectionProps {
  children: React.ReactNode;
  description?: React.ReactNode;
  title: string;
}

const ShortcutSection = ({
  children,
  description,
  title,
}: ShortcutSectionProps) => {
  return (
    <div className={styles.sectionContainer}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <div className={styles.sectionDescription}>{description}</div>
      <ul className={styles.shortcutsList}>{children}</ul>
    </div>
  );
};

interface ShortcutRowProps {
  children?: React.ReactNode;
  name: React.ReactNode;
  keys?: string;
}

const ShortcutRow = ({ children, name, keys }: ShortcutRowProps) => (
  <li className={styles.rowContainer}>
    <span className={styles.rowName}>{name}</span>
    <span>{children || <Shortcut keys={keys} />}</span>
  </li>
);

interface SeparatorProps {
  children: React.ReactNode;
  muted?: boolean;
  mono?: boolean;
  small?: boolean;
}

const ShortcutSeparator = ({
  children,
  muted,
  mono,
  small,
}: SeparatorProps) => (
  <span
    className={classNames(styles.separator, {
      [styles.muted]: muted,
      [styles.small]: small,
      [styles.mono]: mono,
    })}
  >
    {children}
  </span>
);

const KeyboardShortcutsPanel = () => {
  const isPublicView = useIsPublicView();
  const isPublicDocumentationView = useIsPublicDocumentationView();

  const arrow = (
    <ShortcutSeparator muted mono>
      →
    </ShortcutSeparator>
  );

  return (
    <RightPanel.Inner
      header={<RightPanel.NewHeader title="Keyboard shortcuts" />}
    >
      <ShortcutSection
        title="Everywhere"
        description="It can be used globally, no matter where you are in the app."
      >
        <ShortcutRow name="Show shortcut panel" keys="mod+/" />
        {!isPublicView && (
          <>
            <ShortcutRow name="Focus search bar" keys="/" />
            <ShortcutRow name="Create new post" keys="shift+n" />
            <ShortcutRow name="Jump to post" keys="mod+k" />
          </>
        )}
        {isPublicDocumentationView && (
          <ShortcutRow name="Open search" keys="mod+k" />
        )}
      </ShortcutSection>
      {!isPublicDocumentationView && (
        <>
          <ShortcutSection
            title="Editing"
            description="It’s specific to editing content inside a post."
          >
            {!isPublicView && (
              <ShortcutRow name="Enter edit mode">
                <Shortcut keys="e" />
                <ShortcutSeparator muted>or</ShortcutSeparator>
                <Shortcut keys="i" />
              </ShortcutRow>
            )}
            <ShortcutRow name="Exit edit mode" keys="mod+enter" />
            <ShortcutRow name="Undo" keys="mod+z" />
            <ShortcutRow name="Redo" keys="mod+shift+z" />
            <ShortcutRow name="Remove formatting" keys="mod+option+0" />
            <ShortcutRow name="Heading 1" keys="mod+option+1" />
            <ShortcutRow name="Heading 2" keys="mod+option+2" />
            <ShortcutRow name="Ordered list" keys="mod+shift+7" />
            <ShortcutRow name="Bullet list" keys="mod+shift+8" />
            <ShortcutRow name="Check list" keys="mod+shift+9" />
            <ShortcutRow name="Bold" keys="mod+b" />
            <ShortcutRow name="Italic" keys="mod+i" />
            <ShortcutRow name="Strikethrough" keys="mod+option+x" />
            <ShortcutRow name="Add link" keys="mod+k" />
            <ShortcutRow name="Add comment">
              <Shortcut keys="mod+option+m" />
              <ShortcutSeparator muted>or</ShortcutSeparator>
              <div className={styles.secondRow}>
                <Shortcut keys="mod+option+a" />
              </div>
            </ShortcutRow>
            <ShortcutRow name="Copy as markdown" keys="mod+shift+c" />
            <ShortcutRow name="Slash commands" keys="/" />
            <ShortcutRow name="Mention person" keys="@" />
            <ShortcutRow name="Link to post" keys="++" />
            <ShortcutRow name="Insert emoji" keys=":" />
          </ShortcutSection>
          <ShortcutSection
            title="Markdown"
            description="Works only in editor mode within a post."
          >
            <ShortcutRow name="Heading 1">
              <Shortcut keys="#" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Heading 2">
              <Shortcut keys="#+#" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Ordered list">
              <Shortcut keys="1+." small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Bullet list">
              <Shortcut keys="-" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Check list">
              <Shortcut keys="[+]" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Code block">
              <Shortcut keys="`+`+`" small />
              {arrow}
              <Shortcut keys="enter" small />
            </ShortcutRow>
            <ShortcutRow name="Blockquote">
              <Shortcut keys=">" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Horizontal Rule">
              <Shortcut keys="---" small />
            </ShortcutRow>
            <ShortcutRow name="Bold">
              <Shortcut keys="*+*" small />
              <ShortcutSeparator small>text</ShortcutSeparator>
              <Shortcut keys="*+*" small />
            </ShortcutRow>
            <ShortcutRow name="Italic">
              <Shortcut keys="*" small />
              <ShortcutSeparator small>text</ShortcutSeparator>
              <Shortcut keys="*" small />
            </ShortcutRow>
            <ShortcutRow name="Strikethough">
              <Shortcut keys="~+~" small />
              <ShortcutSeparator small>text</ShortcutSeparator>
              <Shortcut keys="~+~" small />
            </ShortcutRow>
            <ShortcutRow name="Inline code">
              <Shortcut keys="`" small />
              <ShortcutSeparator small>code</ShortcutSeparator>
              <Shortcut keys="`" small />
            </ShortcutRow>
          </ShortcutSection>
        </>
      )}
    </RightPanel.Inner>
  );
};

export default KeyboardShortcutsPanel;
